<template>
  <card-canvas class="company-create">
    <card-canvas-two-deep-breadcrumb
      :parent-route="RouteAssistant.getPeopleRoute($route.params.accountId)"
      child-name="Create Company"
      parent-name="Users" />
    <!-- Company Name -->
    <masthead-input
      v-model="formData.name"
      :is-focused="true"
      placeholder="Enter Company Name..."
      maxlength="50"
    />
    <h3>Invite Users</h3>
    <p>
      Enter the email addresses and account roles for all the users you want to add. 
      <strong>Admins</strong> can invite new users to the account, create new Projects &amp; Mailboxes and manage their settings.
      <strong>Owners</strong> can additionally access account settings and billing.
    </p>
    <!-- Users form -->
    <el-form class="company-create__form">
      <div v-for="(item, index) in formData.listInvitees.length " :key="index" class="company-create__form--row">
        <el-form-item class="col-7">
          <standard-email-input 
            v-model="formData.listInvitees[index].email" 
            placeholder="Invite by email address..."
            @invalid-on-blur="addInvalidEmailIndex(index)"
            @valid-on-blur="addValidEmailIndex(index)"
          />
        </el-form-item>
        <el-form-item class="col-3">
          <single-select v-model="formData.listInvitees[index].accountRoleID" placeholder="Account role">
            <el-option v-for="role in availableRoles" :key="role.id" :label="role.name" :value="role.id" />
          </single-select>
        </el-form-item>
      </div>
      <dd-button class="add-another" type="text" @click="addRow()">+ Add Another</dd-button>
    </el-form>
    <dd-button 
      :is-loading="isSubmitting" 
      :is-disabled="disableSubmit"
      size="large" 
      color="blue" 
      @click="createCompany">
      {{ submitLabel }}
    </dd-button>
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import CardCanvas from '@/layouts/card-canvas.vue'

/* Mixins */

/* Components */
import MastheadInput from '@/components/global/inputs/masthead-input.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { AccountRoles } from '@/types/defined-types'
import {
  ICompaniesInputCreate,
  ICompaniesInputCreateInvitee,
} from '@/api/types/request.types'
import { IItemForSelection } from '@/api/types/response.types'

/* API */
import CompaniesAPI from '@/api/modules/companies.api'

export default defineComponent({
  name: 'CompanyCreate',

  components: {
    CardCanvas,
    MastheadInput,
  },

  data() {
    return {
      isSubmitting: false,
      AccountRoles,
      RouteAssistant,
      invalidEmailRows: [] as number[],
      formData: {
        name: '',
        sendInvitations: true,
        /* Stub with a default invitee */
        listInvitees: [
          {
            email: '',
            accountRoleID: 1,
          },
          {
            email: '',
            accountRoleID: 1,
          },
          {
            email: '',
            accountRoleID: 1,
          },
        ],
      } as ICompaniesInputCreate,
    }
  },

  head: {
    title: 'Create Company',
  },

  computed: {
    lastInvitee(): ICompaniesInputCreateInvitee {
      return this.formData.listInvitees[this.formData.listInvitees.length - 1]
    },
    availableRoles(): IItemForSelection[] {
      return this.$permissions.availableRolesToAdmin
    },
    submitLabel(): string {
      if (this.formData.listInvitees.filter(i => i.email !== '').length > 0) {
        return 'Create Company & Send Invites'
      }

      return 'Create Company'
    },
    disableSubmit(): boolean {
      return !(
        this.formData.name.trim() !== '' && this.invalidEmailRows.length === 0
      )
    },
  },

  methods: {
    addRow(): void {
      /* Stub new row with blank email but carryover other columns */
      this.formData.listInvitees.push({
        email: '',
        accountRoleID: this.lastInvitee.accountRoleID,
      })
    },
    addInvalidEmailIndex(index: number) {
      // Add the index from the invalid list...
      if (this.invalidEmailRows.includes(index)) {
        return
      }
      this.invalidEmailRows.push(index)
    },
    addValidEmailIndex(index: number) {
      // Remove the index from the invalid list...
      if (this.invalidEmailRows.includes(index)) {
        this.invalidEmailRows = this.invalidEmailRows.filter(e => e !== index)
      }
    },
    async createCompany() {
      this.isSubmitting = true
      try {
        const response = await CompaniesAPI.post_Create(this.formData)
        this.$messaging.showSuccess(response.message)
        RouteAssistant.goToPeople(this.$router, this.$route.params.accountId)
      } catch (error) {
        this.$error.emit(error, true)
      }
      this.isSubmitting = false
    },
  },
})
</script>

<style lang="scss">
.company-create {
  h3 {
    margin-top: 30px;
  }

  &__form {
    margin: 16px 0;

    .add-another {
      padding: 0;
    }

    &--row {
      display: flex;
      align-items: center;

      > .el-form-item {
        // Override bootstrap grid padding
        padding: 8px 15px 8px 0 !important;
        // Override el-form-item
        margin-bottom: 0 !important;
      }
    }
  }
}
</style>
